/**
 * $computed catalog functions for DramaDetector SDUI
 *
 * These functions are registered in the json-render catalog and available
 * to $computed expressions in UI specs. The LLM can use them to generate
 * dynamic formatting without server round-trips.
 *
 * Usage in a spec:
 *   { "$computed": "formatScore", "args": { "score": 7.2 } }
 *   { "$computed": "tierCopy", "args": { "copies": { "free": "Upgrade", "drama_plus": "View" }, "tier": { "$state": "/tier" } } }
 *
 * Task: [#1158.P1.5]
 * Agent: Diego <diego@liftping.net>
 */

import type { ComputedFunction } from "@json-render/core";
import { pluralize as sharedPluralize } from "@dramadetector/shared";

// ============================================================================
// formatScore
// ============================================================================

/**
 * Format a drama score with sign and context.
 *
 * Modes (based on args):
 * - score only: "7.2/10"
 * - score + delta: "+2.3 points improved" / "-1.5 points"
 * - score = 0: "0.0/10"
 *
 * @param args.score - The drama score (0-10)
 * @param args.delta - Optional score change (positive = improved)
 */
export function formatScore(args: Record<string, unknown>): string {
  const score = Number(args.score ?? 0);
  const delta = args.delta != null ? Number(args.delta) : undefined;

  // Clamp score to valid range
  const clampedScore = Math.max(0, Math.min(10, score));
  const formatted = clampedScore.toFixed(1);

  if (delta != null) {
    const sign = delta > 0 ? "+" : "";
    const deltaFormatted = `${sign}${delta.toFixed(1)}`;
    if (delta > 0) {
      return `${deltaFormatted} points improved`;
    }
    if (delta < 0) {
      return `${deltaFormatted} points`;
    }
    return `${formatted}/10`;
  }

  return `${formatted}/10`;
}

// ============================================================================
// formatCurrency
// ============================================================================

/** Module-level cache for Intl.NumberFormat instances (keyed by currency code) */
const formatterCache = new Map<string, Intl.NumberFormat>();

/**
 * Locale-aware currency formatting for pricing displays.
 *
 * @param args.amount - The amount to format
 * @param args.currency - ISO 4217 currency code (default: "USD")
 */
export function formatCurrency(args: Record<string, unknown>): string {
  const amount = Number(args.amount ?? 0);
  const currency = typeof args.currency === "string" ? args.currency : "USD";

  // Handle NaN/Infinity gracefully
  if (!isFinite(amount)) {
    return "$0.00";
  }

  try {
    let formatter = formatterCache.get(currency);
    if (!formatter) {
      formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      formatterCache.set(currency, formatter);
    }
    return formatter.format(amount);
  } catch {
    // Fallback for invalid currency codes
    return `${amount.toFixed(2)} ${currency}`;
  }
}

// ============================================================================
// tierCopy
// ============================================================================

/**
 * Return different copy based on user subscription tier.
 *
 * Useful for tier-conditional CTAs and messaging without
 * generating separate specs per tier.
 *
 * @param args.copies - Record mapping tier names to copy strings
 * @param args.tier - The user's current tier
 */
export function tierCopy(args: Record<string, unknown>): string {
  const copies = args.copies as Record<string, string> | undefined;
  const tier = typeof args.tier === "string" ? args.tier : "free";

  if (!copies || typeof copies !== "object") {
    return "";
  }

  // Exact match first, then fallback to "free", then first available value
  if (tier in copies) {
    return String(copies[tier]);
  }
  if ("free" in copies) {
    return String(copies.free);
  }

  const values = Object.values(copies);
  return values.length > 0 ? String(values[0]) : "";
}

// ============================================================================
// pluralize
// ============================================================================

/**
 * Pluralize count-based strings.
 * Delegates to @dramadetector/shared's pluralize for consistency.
 *
 * @param args.count - The number to check
 * @param args.singular - Singular form (e.g., "pattern")
 * @param args.plural - Plural form (e.g., "patterns")
 */
export function pluralize(args: Record<string, unknown>): string {
  const count = Number(args.count ?? 0);
  const singular = typeof args.singular === "string" ? args.singular : "";
  const plural = typeof args.plural === "string" ? args.plural : undefined;

  return sharedPluralize(count, singular, plural);
}

// ============================================================================
// Catalog functions map — for registration in DDRenderer
// ============================================================================

/**
 * All DD computed functions, ready to pass to JSONUIProvider or Renderer
 * via the `functions` prop.
 */
export const ddComputedFunctions: Record<string, ComputedFunction> = {
  formatScore,
  formatCurrency,
  tierCopy,
  pluralize,
};
